import type { BatteryProposalPersonDto } from "~/api/dto/battery-proposal.dto"
import type {
  SolarComputeDataDto,
  SolarComputeIncomeDto,
  SolarComputePayoutDto,
} from "~/api/dto/solar-proposal-compute.dto"
import type { SolarProposalDesignSectionDto } from "~/api/dto/solar-proposal-design.dto"
import type { SolarProposalEquipmentSectionDto } from "~/api/dto/solar-proposal-equipment.dto"
import type { SolarProposalImpactSectionDto } from "~/api/dto/solar-proposal-impact.dto"
import type { SolarProposalPricingDto } from "~/api/dto/solar-proposal-pricing.dto"
import type { SolarProposalSavingsSectionDto } from "~/api/dto/solar-proposal-savings.dto"
import type { SolarProposalDto } from "~/api/dto/solar-proposal.dto"

/**
 * One section's read, as the presentation renders it: the data, still loading,
 * or failed. A section that is not `ready` shows its own notice and never takes
 * the page down with it.
 */
export type SolarSectionState<T> =
  | { readonly status: `error` }
  | { readonly status: `loading` }
  | { readonly status: `ready`; readonly data: T }

/**
 * The header and hero: who the proposal is for, who prepared it, and the
 * design's headline figures. The public projection of `SolarProposalDto`,
 * minus everything that is the seller's (`viewerCanUpdate`, `powurProjectId`,
 * `siteId`).
 */
export type SolarPresentationHeader = {
  readonly id: string
  readonly status: SolarProposalDto[`status`]
  readonly createdAt: string
  readonly homeowner: BatteryProposalPersonDto | null
  readonly address: SolarProposalDto[`address`]
  readonly consultant: BatteryProposalPersonDto
  readonly utilityName: string | null
  readonly offsetPct: number | null
  readonly offsetSource: SolarProposalDto[`offsetSource`]
  /** The offset's consumption includes the AI compute node's modeled load. */
  readonly includesAiComputeLoad: boolean
  readonly powurCareSelected: boolean
  /** The header's proposal badge; null shows none. */
  readonly proposalNumber: string | null
}

/**
 * The pricing summary and its Credit Human financing only: never the raw
 * stored cost or its line items.
 */
export type SolarPresentationPricing = Pick<
  SolarProposalPricingDto,
  `financingSummary` | `pricingStatus` | `summary`
> & {
  /**
   * The seller's LightReach plan: a chosen offer, never the ledger, says what
   * the homeowner pays, so without current terms nothing priced shows. A
   * public read never sets it; it sends a TPO's terms with no summary.
   */
  readonly priceSetByOffer?: boolean
}

/**
 * The selected compute node, as the presentation shows it. `payout` (Hosting
 * Agreement Rev 2.10) is null while a public page withholds the money, or from
 * an older gateway; the section then shows the product without figures.
 * `income` is the node's modeled load; the money is `payout`.
 */
export type SolarPresentationAiCompute = {
  readonly name: string
  readonly computerData: SolarComputeDataDto | null
  readonly income: SolarComputeIncomeDto | null
  readonly payout: SolarComputePayoutDto | null
}

/**
 * Everything the Solar presentation renders, in one prop. The seller page
 * builds it from its authenticated reads; a public link builds it from the
 * public projection. Seller-only data (edit entry points, Refresh Pricing,
 * Setup) never travels here: it arrives through the presentation's slots.
 */
export type SolarPresentationData = {
  readonly header: SolarPresentationHeader
  readonly design: SolarSectionState<SolarProposalDesignSectionDto>
  readonly equipment: SolarSectionState<SolarProposalEquipmentSectionDto>
  readonly savings: SolarSectionState<SolarProposalSavingsSectionDto>
  readonly pricingSummary: SolarSectionState<SolarPresentationPricing>
  readonly impact: SolarSectionState<SolarProposalImpactSectionDto>
  /** The selected AI compute node; null with none, or until its read answers. */
  readonly aiCompute: SolarPresentationAiCompute | null
}

/** Keys a recipient must never be sent, at any depth. */
export const SELLER_ONLY_PRESENTATION_KEYS = [
  `viewerCanUpdate`,
  `powurProjectId`,
  `siteId`,
  `pricing`,
  `lineItems`,
  `baseCost`,
  `margin`,
  `marginPerWatt`,
  `sellerId`,
  `mentorId`,
  `permissions`,
  `contracts`,
] as const

/** Every key reachable in `T`, at any depth. */
type DeepKeys<T> = T extends readonly (infer Item)[]
  ? DeepKeys<Item>
  : T extends object
    ? { [K in keyof T]-?: DeepKeys<T[K]> | K }[keyof T]
    : never

/**
 * Compile-time guard, checked by the type check rather than a test (test files
 * are not type-checked): adding a seller-only field to `SolarPresentationData`,
 * at any depth, makes this assignment fail.
 */
type LeakedSellerKeys = Extract<
  DeepKeys<SolarPresentationData>,
  (typeof SELLER_ONLY_PRESENTATION_KEYS)[number]
>
export const PRESENTATION_DATA_HAS_NO_SELLER_KEYS: [LeakedSellerKeys] extends [
  never,
]
  ? true
  : LeakedSellerKeys = true

/**
 * A read as a section state. A failed refetch keeps the data already on
 * screen, so data wins over an error.
 */
export const toSectionState = <T>(query: {
  readonly data: T | undefined
  readonly isError: boolean
}): SolarSectionState<T> =>
  query.data !== undefined
    ? { status: `ready`, data: query.data }
    : query.isError
      ? { status: `error` }
      : { status: `loading` }

/** The section's data once ready, else undefined. */
export const sectionData = <T>(state: SolarSectionState<T>): T | undefined =>
  state.status === `ready` ? state.data : undefined

/** The seller's header read, as the presentation's header. */
export const toPresentationHeader = (
  proposal: SolarProposalDto,
): SolarPresentationHeader => ({
  id: proposal.id,
  status: proposal.status,
  createdAt: proposal.createdAt,
  homeowner: proposal.homeowner,
  address: proposal.address,
  consultant: proposal.consultant,
  utilityName: proposal.utilityName,
  offsetPct: proposal.offsetPct,
  offsetSource: proposal.offsetSource,
  includesAiComputeLoad: proposal.includesAiComputeLoad,
  powurCareSelected: proposal.powurCareSelected,
  proposalNumber: String(proposal.powurProjectId),
})
